import { useRef, useState } from "react";
import Email from "./Email";
import { useParams } from "react-router";
import $api from "../queries/openapi";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";

export default function EditEmails({
  quoteRevisionId,
  disabled = false,
}: {
  quoteRevisionId: string;
  disabled?: boolean;
}) {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const queryPath = "/api/teams/{team_slug}/quote_revisions/{id}/reply_to";
  const mutationPath =
    "/api/teams/{team_slug}/quote_revisions/{id}/reply_to/{email}";

  const queryOptions = {
    params: {
      path: {
        team_slug: teamSlug!,
        id: quoteRevisionId,
      },
    },
  };
  const queryKey = ["get", queryPath, queryOptions];

  const { data: quoteEmails } = $api.useQuery("get", queryPath, queryOptions);
  const emails = quoteEmails?.map((r) => r.email) || [];

  const { mutate: addQuoteEmail } = $api.useMutation("put", mutationPath, {
    onSuccess: () => {
      setInput("");
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: deleteQuoteEmail } = $api.useMutation(
    "delete",
    mutationPath,
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    },
  );

  const [input, setInput] = useState("");

  return (
    <form ref={formRef} className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        {emails.map((email) => (
          <Email
            key={email}
            email={email}
            remove={() =>
              deleteQuoteEmail({
                params: {
                  path: {
                    ...queryOptions.params.path,
                    email: email,
                  },
                },
              })
            }
          />
        ))}
      </div>
      {!disabled && (
        <input
          placeholder="Add email address"
          value={input}
          type="email"
          onChange={(e) => setInput(e.target.value)}
          className={classNames(
            "focus:outline-none border-b focus:border-blue-600",
            {
              "focus:border-red-500 border-red-500":
                emails.length === 0 && input === "",
            },
          )}
          required={emails.length === 0}
          onBlur={() => {
            if (formRef.current?.checkValidity() === false) {
              formRef.current.reportValidity();
              return;
            }

            addQuoteEmail({
              params: {
                path: {
                  ...queryOptions.params.path,
                  email: input,
                },
              },
              body: {
                name: "",
              },
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              e.currentTarget.blur();
            }
          }}
        />
      )}
    </form>
  );
}
